import { Injectable } from '@angular/core';
import { NotificationService } from '../../../../../../common/services/notification/notification.service';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { UliBriefModel } from '../models/uli-brief.model';
import { UliBriefService } from './uli-brief.service';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { UliBriefInitialState, UliBriefLoad } from './uli-brief.actions';
import { ActionBriefModel } from '../../models';
import { AllocationViewActionsFacade } from '../../actions';
import { CustomHttpErrorResponse, Error } from '../../../../../../common/constants/error';
import { BriefLocationInfoModel } from '../../models/allocation-brief-models';
import { HubBrief, HubContextFacade } from '../../../../administration';
import { AllocationViewActionIdentifierTypeEnum } from '../../constants';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface UliBriefStateModel {
  uli: UliBriefModel;
  actionBriefDetails: ActionBriefModel;
  loaded: boolean;
}

const initialState: UliBriefStateModel = {
  uli: null,
  actionBriefDetails: null,
  loaded: false,
};

@State({
  name: 'uliBrief',
  defaults: initialState,
})
@Injectable()
export class UliBriefState {
  constructor(
    private uliBriefService: UliBriefService,
    private allocationViewActionsFacade: AllocationViewActionsFacade,
    private notificationService: NotificationService,
    private hubContextFacade: HubContextFacade,
  ) {}

  @Selector()
  static getLoaded(state: UliBriefStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static getUliBrief(state: UliBriefStateModel): UliBriefModel {
    return state.uli;
  }

  @Selector()
  static getLocationInfo(state: UliBriefStateModel): BriefLocationInfoModel {
    return { compartmentId: state.uli.allocatedCompartmentId ?? state.uli.compartmentId, dockId: state.uli.dockId };
  }

  @Selector()
  static getActionBriefDetails(state: UliBriefStateModel): ActionBriefModel {
    return state.actionBriefDetails;
  }

  @Action(UliBriefLoad)
  getUliBrief(
    ctx: StateContext<UliBriefStateModel>,
    { payload }: UliBriefLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.hubContextFacade.selectedHub$.pipe(
      filter(Boolean),
      map((hub: HubBrief) => hub.id),
      switchMap((hubId: number) =>
        this.uliBriefService.getUliBrief(payload.compartmentId, payload.type, payload.uli, hubId),
      ),
      take(1),
      map((uli: UliBriefModel) => {
        const actionBriefDetails = new ActionBriefModel({
          hubId: uli.hubId,
          truckId: uli.truckId,
          slotId: uli.slotId,
          compartmentId: uli.compartmentId,
          allocatedCompartmentId: uli.allocatedCompartmentId,
          uli: uli.uli,
          type: uli.type,
          actionOrigin: AllocationViewActionIdentifierTypeEnum.Uli,
        });

        this.allocationViewActionsFacade.setLeadUlis([
          {
            uli: uli?.uli,
            activityTpe: uli?.type,
            compartmentId: uli?.allocatedCompartmentId ?? uli?.compartmentId,
          },
        ]);
        ctx.patchState({ uli, actionBriefDetails, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(UliBriefInitialState)
  setInitialState(ctx: StateContext<UliBriefStateModel>): void {
    ctx.setState(initialState);
  }
}
